import { DateTime } from "luxon";
import { SchoolHoursModel, SchoolYearType, ShortenedDays } from "model";

type CalcTimeProps = {
  hours: SchoolHoursModel;
  time: DateTime;
  type: "start" | "end";
};

/**
 * Calculates the start or end time for school hours on a specific day.
 * Converts a 4-digit time format (e.g., 830 for 8:30) to ISO string format.
 *
 * @param props - Configuration object for time calculation
 * @param props.hours - School hours model containing time data for each day
 * @param props.time - DateTime object representing the target date
 * @param props.type - Whether to calculate "start" or "end" time
 * @returns ISO string representation of the calculated time, or null if conversion fails
 *
 * @example
 * ```ts
 * const hours = { mon: { start: 830, end: 1530 } };
 * const time = DateTime.local(2024, 9, 2); // Monday
 * const startTime = calcHours({ hours, time, type: "start" });
 * // Returns ISO string for 8:30 AM on that Monday
 * ```
 */
export const calcHours = (props: CalcTimeProps) => {
  const { hours, time, type } = props;

  const weekday = time.weekdayShort.toLowerCase() as ShortenedDays;

  const newDateTime = time
    .set({
      hour: parseInt(hours[weekday][type].toString().padStart(4, "0").slice(0, 2)),
      minute: parseInt(hours[weekday][type].toString().padStart(4, "0").slice(2, 5)),
    })
    .toISO();

  return newDateTime;
};

/**
 * Finds the current school year from a list of school years.
 *
 * @returns {{ start: string, end: string, id?: string } | undefined} - The matched school year or undefined if none found.
 */
export function getSchoolYear(args: {
  now: DateTime;
  year: "currentYear" | "nextYear";
  existingYears?: Array<SchoolYearType>;
}): { start: string; end: string; id?: string } {
  const { now, year, existingYears } = args;

  const currentSchoolYearStartYear =
    now < DateTime.fromObject({ year: now.year, month: 7, day: 1 }) ? now.year - 1 : now.year;

  const startYear =
    year === "currentYear" ? currentSchoolYearStartYear : currentSchoolYearStartYear + 1;

  const schoolYearStart = DateTime.fromObject({ year: startYear, month: 7, day: 1 });
  const schoolYearEnd = DateTime.fromObject({ year: startYear + 1, month: 6, day: 30 });

  const foundYear = existingYears?.find((y) => {
    const start = DateTime.fromISO(y.start);
    const end = DateTime.fromISO(y.end);

    return start >= schoolYearStart && end <= schoolYearEnd;
  });

  return foundYear ?? { start: "", end: "" };
}
